import { useTheme } from '../../context/ThemeContext';
import { AvatarImage } from './AvatarImage';
import { formatDate } from '@/utils/useUtil';

export interface NormalizedComment {
  commentId: string | number;
  userName: string;
  specialtyName?: string;
  commentText: string;
  createdAt: string;
  profilePhotoId?: number | null;
}

function formatCommentTimestamp(dateString: string) {
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return '';
  const now = Date.now();
  const diff = Math.max(0, now - parsed.getTime());
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CommentItem({ comment }: Readonly<{ comment: NormalizedComment }>) {
  const { theme } = useTheme();

  return (
    <div className="flex gap-3">
      <AvatarImage
        profilePhotoId={comment.profilePhotoId}
        name={comment.userName}
        alt={comment.userName}
        size={44}
        className="flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>
            {comment.userName}
          </p>
          <span className="text-xs" style={{ color: theme.textSecondary }}>
            {formatDate(comment.createdAt.toLocaleString())}
          </span>
        </div>
        {comment.specialtyName ? (
          <p className="text-xs mt-1 truncate" style={{ color: theme.textSecondary }}>
            {comment.specialtyName}
          </p>
        ) : null}
        <p className="mt-2 text-sm leading-6 whitespace-pre-line" style={{ color: theme.text }}>
          {comment.commentText}
        </p>
      </div>
    </div>
  );
}
